import AppAlert from '@nxsys/components/AppAlert';
import AppButton from '@nxsys/components/AppButton';
import { useDrawerSave } from './useDrawerSave';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppLoader from '@nxsys/components/AppLoader';
import AppSelect from '@nxsys/components/AppSelect';
import BankSelectInput from '@nxsys/components/BankSelectInput';
import AppSwitch from '@nxsys/components/AppSwitch';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import {
  useGetBankInformationByEmployeeId,
  useUpdateBankByEmployeeId,
} from '@nxsys/hooks/api';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { verifyBankAccount } from '@nxsys/services/api/bank';
import { Icons } from '@nxsys/types/icons';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { showMessage } from '@nxsys/utils/message';
import { IRELAND } from '@nxsys/store/constants/keys';
import { countryList } from '@nxsys/utils';
import { Col, Form, Row, Typography } from 'antd';
import { AppState } from '@nxsys/store';
import { useEffect, useMemo, useState } from 'react';
import { useSelector as useReduxSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

type Props = {
  data: EmployeeRecordType | undefined;
  readOnly?: boolean;
  employeeId?: string | null;
};

const BankInformation = ({
  data: employeeData,
  readOnly,
  employeeId: employeeIdProp,
}: Props) => {
  const { messages, formatMessage } = useTypedIntl();
  const params = useParams();
  const employeeId = employeeIdProp ?? params?.employeeId ?? null;
  const { payrollCountry } = useReduxSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const [isInternalAccount, setIsInternalAccount] = useState(false);

  const [form] = Form.useForm();

  const {
    run: fetchBankInformation,
    loading: isFetchingBankInformation,
    value: bankInformationValue,
  } = useGetBankInformationByEmployeeId();
  const { run: updateBank, loading: isUpdatingBank } =
    useUpdateBankByEmployeeId();
  const bankData = useMemo(
    () => bankInformationValue?.data,
    [bankInformationValue],
  );

  useEffect(() => {
    if (employeeId) {
      fetchBankInformation(employeeId);
    }
  }, [employeeId]);

  useEffect(() => {
    if (bankData && !isFetchingBankInformation) {
      form.setFieldsValue({
        bankName: bankData.bankName
          ? { label: bankData.bankName, value: bankData.bankName }
          : null,
        accountName: bankData.accountName ?? '',
        sortCode: bankData.sortCode ?? '',
        accountNumber: bankData.accountNumber ?? '',
        buildingSocietyNumber: bankData.buildingSocietyNumber ?? '',
        IBANReference: bankData.IBANReference ?? '',
        SWIFTNo: bankData.SWIFTNo ?? '',
        country: bankData.country
          ? (countryList.find((c) => c.value === bankData.country) ?? null)
          : null,
      });
      if (bankData.isInternalAccount) {
        setIsInternalAccount(true);
      }
    }
  }, [bankData, isFetchingBankInformation]);

  const [verifyBankLoading, setVerifyBankLoading] = useState(false);
  const [verifyBankResult, setVerifyBankResult] = useState<{
    status: 'success' | 'warning';
    message: string;
  } | null>(null);

  const handleVerifyBank = async () => {
    const accountNumber = form.getFieldValue('accountNumber');
    const sortCode = form.getFieldValue('sortCode');
    if (!accountNumber || !sortCode) {
      showMessage({
        type: 'warning',
        content: messages['form.pleaseEnter'],
      });
      return;
    }
    try {
      setVerifyBankLoading(true);
      verifyBankAccount({
        params: { AccountNumbers: accountNumber, SortCodes: sortCode },
        cb: (data: any) => {
          if (!data) return;
          if (!data?.Error) {
            setVerifyBankResult({
              status: 'success',
              message: formatMessage(
                { id: 'common.verifyBankSuccessWithName' },
                { name: employeeData!.fullName || '' },
              ),
            });
          } else {
            const { Cause, Description, Resolution } = data;
            setVerifyBankResult({
              status: 'warning',
              message: `${Cause} ${Description}. ${Resolution}`,
            });
          }
          setVerifyBankLoading(false);
        },
      });
    } catch (err) {
      console.error(err);
      setVerifyBankLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (!employeeData?.id) return;

      const params = {
        bankName: values.bankName?.value || '',
        accountName: values.accountName,
        accountNumber: values.accountNumber,
        sortCode: values.sortCode,
        buildingSocietyNumber: values.buildingSocietyNumber,
        IBANReference: values.IBANReference,
        SWIFTNo: values.SWIFTNo,
        country: values.country?.value || null,
        isInternalAccount,
      };

      await updateBank(employeeData.id, params);
      showMessage({
        type: 'success',
        content: messages['common.updateSuccess'],
      });
    } catch (error: any) {
      if (error?.errorFields) return;
      console.error(error);
    }
  };

  // NX-4682: in the quick-edit drawer, surface Save in the drawer header.
  const inDrawer = useDrawerSave(handleSave, isUpdatingBank, !readOnly);

  if (!employeeData || isFetchingBankInformation)
    return (
      <div className="relative h-[100px]">
        <AppLoader />
      </div>
    );

  return (
    <div>
      {verifyBankResult && (
        <div className="mb-4">
          <AppAlert
            type={verifyBankResult.status}
            variant={verifyBankResult.status}
            message={verifyBankResult.message}
          />
        </div>
      )}

      <Typography.Title
        level={5}
        className="!mb-8 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
      >
        {messages['common.bankDetails'] ?? 'Bank Details'}
      </Typography.Title>

      {isIreland && (
        <div className="mb-4 flex items-center gap-2">
          <AppSwitch
            checked={isInternalAccount}
            onChange={(checked) => {
              setIsInternalAccount(checked);
              form.validateFields();
            }}
            disabled={readOnly}
          />
          <span>
            {messages['common.internationalAccount'] ?? 'International Account'}
          </span>
        </div>
      )}

      <AppFormContainer
        form={form}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        disabled={readOnly}
      >
        {/* Bank Name */}
        <Form.Item label={messages['common.bankName']} name="bankName">
          <BankSelectInput
            className="w-full !min-w-0"
            placeholder={messages['form.select']}
          />
        </Form.Item>

        {/* Account Name */}
        <Form.Item
          label={messages['common.accountName']}
          name="accountName"
          rules={[
            {
              required: true,
              whitespace: true,
              message: messages['form.pleaseEnter'],
            },
          ]}
        >
          <AppInput placeholder="e.g. John Smith" />
        </Form.Item>

        {/* Sort Code | Account Number */}
        <Row gutter={24}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={messages['common.sortCode6Digits']}
              name="sortCode"
              rules={[
                ...(!isIreland && !isInternalAccount
                  ? [
                      {
                        required: true,
                        message: messages['form.pleaseEnter'],
                      },
                    ]
                  : []),
                {
                  pattern: /^\d{6}$/,
                  message: messages['form.sortCodeMustBe6Digits'],
                },
              ]}
              normalize={(value: string) => value.replace(/[^0-9]/g, '')}
            >
              <AppInput placeholder={messages['form.enter']} maxLength={6} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label={messages['common.accountNumber8Digits']}
              name="accountNumber"
              rules={[
                ...(!isIreland && !isInternalAccount
                  ? [
                      {
                        required: true,
                        message: messages['form.pleaseEnter'],
                      },
                    ]
                  : []),
                {
                  pattern: /^\d{8}$/,
                  message: messages['form.accountNumberMustBe8Digits'],
                },
              ]}
              normalize={(value: string) => value.replace(/[^0-9]/g, '')}
            >
              <AppInput placeholder={messages['form.enter']} maxLength={8} />
            </Form.Item>
          </Col>
        </Row>

        {/* Building Society Number */}
        <Form.Item
          label={messages['common.buildingSocietyNumberUpto24Digits']}
          name="buildingSocietyNumber"
          normalize={(value: string) => value.replace(/[^0-9]/g, '')}
        >
          <AppInput placeholder={messages['form.enter']} maxLength={24} />
        </Form.Item>

        {/* IBAN Reference | SWIFT No */}
        <Row gutter={24}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={messages['common.IBANReferenceUpto30Digits']}
              name="IBANReference"
              rules={
                isIreland || isInternalAccount
                  ? [{ required: true, message: messages['form.pleaseEnter'] }]
                  : []
              }
              normalize={(value: string) =>
                value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
              }
            >
              <AppInput placeholder={messages['form.enter']} maxLength={30} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label={messages['common.SWIFTNoUpto20Digits']}
              name="SWIFTNo"
              rules={
                !isIreland && isInternalAccount
                  ? [{ required: true, message: messages['form.pleaseEnter'] }]
                  : []
              }
              normalize={(value: string) =>
                value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
              }
            >
              <AppInput placeholder={messages['form.enter']} maxLength={20} />
            </Form.Item>
          </Col>
        </Row>

        {/* Country */}
        <Form.Item
          label={messages['common.country']}
          name="country"
          rules={
            !isIreland && isInternalAccount
              ? [{ required: true, message: messages['form.pleaseSelect'] }]
              : []
          }
        >
          <AppSelect
            options={countryList}
            className="w-full !min-w-0"
            placeholder={messages['form.selectCountry']}
          />
        </Form.Item>

        {/* Action Buttons */}
        {!readOnly && !inDrawer && (
          <div className="flex items-center justify-start gap-3 pt-4">
            <AppButton
              type="primary"
              name={messages['common.save']}
              leftIcon={
                <Icons.IconCheckCircle
                  color={defaultTheme.theme.palette.white}
                />
              }
              onClick={handleSave}
              loading={isUpdatingBank}
            />
            {!isIreland && (
              <AppButton
                variant="outlined"
                name={messages['common.verify']}
                onClick={handleVerifyBank}
                loading={verifyBankLoading}
                disabled={verifyBankLoading}
              />
            )}
          </div>
        )}
      </AppFormContainer>
    </div>
  );
};

export default BankInformation;
